/* ============================================================
   The single impact-score control: a wide, centered 4-stop
   severity slider that lives on its own row below the filters.
   It writes the shared `gdeltFilters.impactScoreMin`, so it
   drives the grid + aggregation strip while the scan map reads
   the same threshold to reveal signal from noise as it sweeps.
   ============================================================ */
import { Box, Typography } from "@mui/material"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import { useEffect, useMemo, useRef } from "react"
import { extraColors } from "../../../utils/theme"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import { getImpact, V2_SEVERITY_MINS } from "./biothreatV2Signal"
import {
  V2_EYEBROW,
  V2_FONT_MONO,
  V2_SEVERITY_COLOR,
  V2_SEVERITY_LABEL,
  V2_SURFACE,
  type V2Tier,
} from "./biothreatV2Tokens"

/** Ascending tiers — the four slider stops, low (left) to severe (right). */
const TIERS: V2Tier[] = ["low", "moderate", "high", "severe"]

const MIN = V2_SEVERITY_MINS.low
const MAX = V2_SEVERITY_MINS.severe
/** Seed when the user has no impact floor yet — the mid "moderate" stop. */
export const DEFAULT_IMPACT_THRESHOLD = V2_SEVERITY_MINS.moderate

const STOPS = TIERS.map((tier, index) => ({
  tier,
  value: V2_SEVERITY_MINS[tier],
  label: V2_SEVERITY_LABEL[tier],
  color: V2_SEVERITY_COLOR[tier],
  pct: (index / (TIERS.length - 1)) * 100,
}))

const TRACK_GRADIENT = `linear-gradient(90deg, ${STOPS.map(
  stop => `${stop.color} ${stop.pct}%`
).join(", ")})`

type Stop = (typeof STOPS)[number]

/** Snap an arbitrary impact value to the nearest tier stop (always defined). */
const nearestStop = (raw: number): Stop =>
  STOPS.reduce((best, stop) =>
    Math.abs(stop.value - raw) < Math.abs(best.value - raw) ? stop : best
  )

const nearestStopValue = (raw: number): number => nearestStop(raw).value

export const BiothreatV2ImpactSlider: React.FC<{
  /** Full unfiltered (noise) field, used only for the live signal/raw readout. */
  mapRows: BiothreatSignal[]
}> = ({ mapRows }) => {
  const { gdeltFilters, setGdeltFilters } = usePrecomputedBiothreatSignals()
  const barRef = useRef<HTMLDivElement>(null)

  // Seed the shared floor once when empty so the slider, grid, strip and map
  // all agree on a starting tier (this control is the single source of truth).
  const seededRef = useRef(false)
  useEffect(() => {
    if (!seededRef.current && gdeltFilters.impactScoreMin === "") {
      seededRef.current = true
      setGdeltFilters({
        ...gdeltFilters,
        impactScoreMin: String(DEFAULT_IMPACT_THRESHOLD),
      })
    }
  }, [gdeltFilters, setGdeltFilters])

  const committed = gdeltFilters.impactScoreMin
    ? Number(gdeltFilters.impactScoreMin)
    : DEFAULT_IMPACT_THRESHOLD
  // Always one of the four stops; the handle eases to it via a CSS transition.
  const displayValue = committed
  const fraction = Math.min(1, Math.max(0, (displayValue - MIN) / (MAX - MIN)))

  const signalCount = useMemo(
    () => mapRows.filter(row => getImpact(row) >= displayValue).length,
    [mapRows, displayValue]
  )
  const total = mapRows.length

  const commit = (value: number) => {
    const next = String(value)
    if (next !== gdeltFilters.impactScoreMin) {
      setGdeltFilters({ ...gdeltFilters, impactScoreMin: next })
    }
  }

  /** Snap to the nearest stop for a click/tap anywhere on the track. */
  const snapToClientX = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return
    const f = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    commit(nearestStopValue(MIN + f * (MAX - MIN)))
  }

  const stepBy = (delta: number) => {
    const index = STOPS.indexOf(nearestStop(committed))
    const nextIndex = Math.min(STOPS.length - 1, Math.max(0, index + delta))
    const nextStop = STOPS[nextIndex]
    if (nextStop) commit(nextStop.value)
  }
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault()
      stepBy(-1)
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault()
      stepBy(1)
    }
  }

  const activeStop = nearestStop(displayValue)

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        px: 3,
        py: "18px",
        borderBottom: `1px solid ${V2_SURFACE.hairlineStrong}`,
        background: "rgba(8,11,17,0.4)",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 760 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: 1.25,
          }}
        >
          <Typography sx={{ ...V2_EYEBROW, color: activeStop.color }}>
            Signal Threshold · Impact ≥ {Math.round(displayValue)}
          </Typography>
          <Typography
            sx={{
              fontFamily: V2_FONT_MONO,
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: extraColors.translucent50,
            }}
          >
            <Box component="span" sx={{ color: extraColors.white }}>
              {signalCount.toLocaleString()}
            </Box>{" "}
            signal /{" "}
            <Box component="span" sx={{ color: extraColors.white }}>
              {total.toLocaleString()}
            </Box>{" "}
            raw
          </Typography>
        </Box>

        {/* Interactive track. The whole strip is the hit area so a click or tap
            anywhere animates the handle to the nearest stop. */}
        <Box
          ref={barRef}
          role="slider"
          tabIndex={0}
          aria-label="Signal impact-score threshold"
          aria-valuemin={MIN}
          aria-valuemax={MAX}
          aria-valuenow={Math.round(displayValue)}
          aria-valuetext={activeStop.label}
          onPointerDown={e => snapToClientX(e.clientX)}
          onKeyDown={handleKeyDown}
          sx={{
            position: "relative",
            height: 26,
            cursor: "pointer",
            touchAction: "none",
            outline: "none",
            "&:focus-visible .impact-handle": {
              boxShadow: `0 0 0 3px rgba(255,255,255,0.25)`,
            },
          }}
        >
          {/* Full severity gradient (the colour scale, always visible). */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              height: 10,
              borderRadius: 5,
              background: TRACK_GRADIENT,
            }}
          />
          {/* Dim the portion to the right of the handle (the "unlit" range). */}
          <Box
            sx={{
              position: "absolute",
              left: `${fraction * 100}%`,
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              height: 10,
              borderRadius: "0 5px 5px 0",
              background: "rgba(8,11,17,0.66)",
              pointerEvents: "none",
              transition: "left 0.18s ease",
            }}
          />
          {/* Stop ticks. */}
          {STOPS.map(stop => (
            <Box
              key={stop.tier}
              sx={{
                position: "absolute",
                left: `${stop.pct}%`,
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 3,
                height: 16,
                borderRadius: 1.5,
                background:
                  stop.value <= displayValue
                    ? "rgba(255,255,255,0.85)"
                    : "rgba(255,255,255,0.25)",
                pointerEvents: "none",
              }}
            />
          ))}
          {/* Handle. */}
          <Box
            className="impact-handle"
            sx={{
              position: "absolute",
              left: `${fraction * 100}%`,
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: activeStop.color,
              border: "2px solid #fff",
              boxShadow: `0 0 12px ${activeStop.color}`,
              pointerEvents: "none",
              transition: "left 0.18s ease, background 0.18s ease",
            }}
          />
        </Box>

        {/* Tier labels under the stops. */}
        <Box sx={{ position: "relative", height: 16, mt: 0.75 }}>
          {STOPS.map(stop => (
            <Typography
              key={stop.tier}
              sx={{
                position: "absolute",
                left: `${stop.pct}%`,
                transform:
                  stop.pct === 0
                    ? "translateX(0)"
                    : stop.pct === 100
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
                fontFamily: V2_FONT_MONO,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                whiteSpace: "nowrap",
                color:
                  stop.value === activeStop.value
                    ? stop.color
                    : extraColors.translucent50,
              }}
            >
              {stop.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
